import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import EInfoModal from '@/components/everridge/EInfoModal';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { useNotInvitedOwnersListApi } from '@/features/owners/hooks/useNotInvitedOwnersListApi';
import OwnersInvitationColumns from '@/features/owners/components/OwnersInvitation/OwnersInvitationColumns';
import OwnersInvitationTableWithSubmit from '@/features/owners/components/OwnersInvitationTableWithSubmit';
import { useOwnerInvatationApi } from '@/features/owners/hooks/useOwnerInvatationApi';
import OwnerDetails from '../OwnersDetails/OwnerDetails';

const INVITATION_STEP = 10000;

export enum OwnersActivityButton {
    All,
    Invited,
    NotInvited,
}

const ownersButtonsList = [
    {
        type: OwnersActivityButton.All,
        name: 'main.owners.components.invitation.list.all',
    },
    {
        type: OwnersActivityButton.Invited,
        name: 'main.owners.components.invitation.list.invited',
    },
    {
        type: OwnersActivityButton.NotInvited,
        name: 'main.owners.components.invitation.list.notInvited',
    },
];

type Props = {
    onClose: () => void;
};

const OwnersInvitation: FC<Props> = ({ onClose }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [showDiscardMessage, setShowDiscardMessage] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [activeActivity, setActiveActivity] = useState<OwnersActivityButton>(
        OwnersActivityButton.NotInvited,
    );
    const [ownerId, setOwnerId] = useState('');
    const [showOwnerDetails, setShowOwnerDetails] = useState(false);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();
    const columns = OwnersInvitationColumns();

    const { notInvitedOwnersListData } = useNotInvitedOwnersListApi();
    const { sendInvitationEmail } = useOwnerInvatationApi({ showToast: false });

    const visibleList = useMemo(() => {
        if (!notInvitedOwnersListData) return [];
        if (activeActivity === OwnersActivityButton.All) return notInvitedOwnersListData.items;

        return notInvitedOwnersListData.items.filter((owner) =>
            activeActivity === OwnersActivityButton.Invited ? !!owner.invitedAt : !owner.invitedAt,
        );
    }, [notInvitedOwnersListData, activeActivity]);

    const handleClose = () => {
        setIsOpen(false);
        setShowDiscardMessage(false);
        setTimeout(onClose, 300);
    };

    const sendInvitations = async () => {
        if (!selectedIds.length) return;

        void (async () => {
            for (let i = 0; i < selectedIds.length; i++) {
                const id = selectedIds[i];

                try {
                    await sendInvitationEmail(id);
                } catch (err) {
                    console.error(
                        `${t('main.owners.components.invitation.invitationError')}: ${id}`,
                        err,
                    );
                }

                if (i < selectedIds.length - 1) {
                    await new Promise((resolve) => setTimeout(resolve, INVITATION_STEP));
                }
            }
        })();

        handleClose();
    };

    const handleShowOwnerDetails = (id: string) => {
        setOwnerId(id);
        setShowOwnerDetails(true);
    };

    return (
        <>
            <Sheet open={isOpen} onOpenChange={handleClose}>
                <SheetContent className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
                    <SheetHeader>
                        <SheetDescription />
                        <SheetTitle>
                            {!isMobile && (
                                <SheetClose
                                    className={cn(
                                        'flex items-center justify-center cursor-pointer',
                                    )}
                                >
                                    <MoveLeftIcon />
                                </SheetClose>
                            )}
                            {t('main.owners.components.invitation.title')}
                            {isMobile && (
                                <SheetClose
                                    className={cn(
                                        'ml-auto flex items-center justify-center cursor-pointer',
                                        isMobile &&
                                            'size-11 border border-popover-100 rounded-full',
                                    )}
                                >
                                    <Cancel_01_Icon />
                                </SheetClose>
                            )}
                        </SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 flex flex-col bg-popover-30 overflow-hidden select-none">
                        <div
                            className={cn(
                                'p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
                                isMobile && 'p-4 gap-4',
                            )}
                        >
                            <div
                                className={cn(
                                    'flex gap-4 justify-between items-center',
                                    isMobile && 'flex-col items-start',
                                )}
                            >
                                <h3 className={cn('text-lg font-medium', isMobile && 'text-base')}>
                                    {t('main.owners.components.invitation.subtitle')}
                                </h3>
                                <div className="flex gap-2">
                                    {ownersButtonsList.map(({ type, name }) => (
                                        <Button
                                            key={type}
                                            variant={
                                                activeActivity === type ? 'default' : 'outline'
                                            }
                                            className={cn(
                                                `hover:shadow-none ${
                                                    activeActivity === type
                                                        ? ''
                                                        : 'hover:bg-popover-50'
                                                } `,
                                                isMobile && 'h-9 px-3.5 py-2',
                                            )}
                                            onClick={() => setActiveActivity(type)}
                                        >
                                            {t(name)}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            <OwnersInvitationTableWithSubmit
                                data={visibleList}
                                columns={columns}
                                pagination={false}
                                activity={activeActivity}
                                onSelectedIdsChange={setSelectedIds}
                                showDetails={handleShowOwnerDetails}
                            />
                        </div>
                    </div>
                    <SheetFooter>
                        <Button className="w-full" onClick={() => setShowDiscardMessage(true)}>
                            {t('elements.buttons.sendInvitation')}
                        </Button>
                    </SheetFooter>
                    <Toaster />
                </SheetContent>
            </Sheet>
            {showDiscardMessage && (
                <EInfoModal
                    label={t('main.owners.components.invitation.sendModal.title')}
                    description={t('main.owners.components.invitation.sendModal.description')}
                    submitName={t('elements.buttons.send')}
                    onSubmit={sendInvitations}
                    closeName={t('elements.buttons.cancel')}
                    onClose={() => setShowDiscardMessage(false)}
                />
            )}
            {showOwnerDetails && <OwnerDetails id={ownerId} onClose={setShowOwnerDetails} />}
        </>
    );
};

export default OwnersInvitation;
